import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UsersIcon,
  CurrencyPoundIcon,
  CalendarIcon,
  ChartBarIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
  BriefcaseIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { projectAPI, taskAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EditProjectModal from '../components/modals/EditProjectModal';
import ActionPlan from '../components/ActionPlan';
import KanbanBoard from '../components/KanbanBoard';
import TaskModal from '../components/modals/TaskModal';
import toast from 'react-hot-toast';

function StatusBadge({ status }) {
  const statusColors = {
    planning: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    on_hold: 'bg-orange-100 text-orange-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace('_', ' ').toUpperCase()}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-blue-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityColors[priority] || 'bg-gray-100 text-gray-800'}`}>
      {priority?.toUpperCase()}
    </span>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const { data: project, isLoading, error } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectAPI.getProject(projectId),
  });

  // Auto-show AI Analysis for AI Autopilot projects
  useEffect(() => {
    if (project?.creation_mode === 'ai_autopilot' && !showAIAnalysis) {
      setShowAIAnalysis(true);
    }
  }, [project, showAIAnalysis]);

  // Fetch AI insights when enabled
  const { data: aiInsights, isLoading: aiLoading } = useQuery({
    queryKey: ['ai-insights', projectId],
    queryFn: () => projectAPI.getAIInsights(projectId),
    enabled: showAIAnalysis,
  });

  // Fetch tasks
  const { data: tasks } = useQuery({
    queryKey: ['tasks', projectId],
    queryFn: () => taskAPI.getProjectTasks(projectId),
    enabled: activeTab === 'tasks',
  });

  const deleteMutation = useMutation({
    mutationFn: () => projectAPI.deleteProject(projectId),
    onSuccess: () => {
      // Cancel any pending queries for this project to avoid 404 errors
      queryClient.cancelQueries(['project', projectId]);
      queryClient.removeQueries(['project', projectId]);
      queryClient.invalidateQueries(['projects']);
      toast.success('Project deleted successfully!');
      navigate('/projects');
    },
    onError: () => {
      toast.error('Failed to delete project');
    },
  });

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${project?.title}"?`)) {
      deleteMutation.mutate();
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleAddTask = () => {
    setSelectedTask(null);
    setShowTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const generateTasksMutation = useMutation({
    mutationFn: () => taskAPI.generateTasksFromAI(projectId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['tasks', projectId]);
      toast.success(`Generated ${data.count} tasks from AI!`);
    },
    onError: (error) => {
      toast.error(`Failed to generate tasks: ${error.message}`);
    },
  });

  const handleGenerateTasks = () => {
    if (window.confirm('Generate AI-powered tasks for this project? This will create 5-8 tasks based on your project context.')) {
      generateTasksMutation.mutate();
    }
  };

  const handleDownloadBusinessCase = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Please log in to download files');
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/download-business-case`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = project?.business_case_filename || 'business_case.txt';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Document downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Project not found</h3>
        <button onClick={() => navigate('/projects')} className="mt-4 btn-primary">
          Back to Projects
        </button>
      </div>
    );
  }

  const budgetUtilization = project.budget > 0 ? (project.spent / project.budget) * 100 : 0;

  return (
    <>
      {showEditModal && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showTaskModal && (
        <TaskModal
          projectId={projectId}
          task={selectedTask}
          onClose={handleCloseTaskModal}
        />
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/projects')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{project.title}</h1>
              <p className="text-sm text-gray-600">Project ID: {project.project_id}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAIAnalysis(!showAIAnalysis)}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${showAIAnalysis
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100'
                }`}
            >
              <SparklesIcon className="w-4 h-4" />
              <span>{showAIAnalysis ? 'Hide Action Plan' : 'Show Action Plan'}</span>
            </button>
            <button
              onClick={() => setShowEditModal(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <PencilIcon className="w-4 h-4" />
              <span>Edit</span>
            </button>
            <button onClick={handleDelete} className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium flex items-center gap-2">
              <TrashIcon className="w-4 h-4" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        <div className="flex gap-3">
          <StatusBadge status={project.status} />
          <PriorityBadge priority={project.priority} />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tasks'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Tasks {tasks && `(${tasks.length})`}
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{project.description || 'No description provided.'}</p>
              </div>

              {project.impact_statement && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-blue-900 mb-2">Impact Statement</h2>
                  <p className="text-blue-800">{project.impact_statement}</p>
                </div>
              )}

              {/* AI Action Plan Section */}
              {showAIAnalysis && (
                <div>
                  {aiLoading ? (
                    <div className="bg-white rounded-lg shadow p-12">
                      <div className="flex flex-col items-center justify-center">
                        <LoadingSpinner size="lg" />
                        <p className="text-gray-600 mt-4">Generating your action plan...</p>
                      </div>
                    </div>
                  ) : aiInsights ? (
                    <ActionPlan insights={aiInsights} />
                  ) : (
                    <div className="bg-white rounded-lg shadow p-12">
                      <p className="text-gray-600 text-center">
                        No action plan available. Click "AI Analysis" to generate your personalized project roadmap.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Timeline
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Start Date</p>
                    <p className="text-lg font-medium text-gray-900">
                      {project.start_date ? new Date(project.start_date).toLocaleDateString('en-GB') : 'Not set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">End Date</p>
                    <p className="text-lg font-medium text-gray-900">
                      {project.end_date ? new Date(project.end_date).toLocaleDateString('en-GB') : 'Not set'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <ChartBarIcon className="w-5 h-5" />
                  Progress
                </h2>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Overall Progress</span>
                      <span className="font-medium text-gray-900">{project.overall_progress || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-blue-600 h-3 rounded-full" style={{ width: `${project.overall_progress || 0}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Success Probability</span>
                      <span className="font-medium text-gray-900">{project.probability_of_success || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${(project.probability_of_success || 0) >= 70 ? 'bg-green-600' :
                            (project.probability_of_success || 0) >= 40 ? 'bg-yellow-600' : 'bg-red-600'
                          }`}
                        style={{ width: `${project.probability_of_success || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CurrencyPoundIcon className="w-5 h-5" />
                  Budget
                </h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Budget</p>
                    <p className="text-2xl font-bold text-gray-900">£{(project.budget || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Spent</p>
                    <p className="text-xl font-semibold text-gray-900">£{(project.spent || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Remaining</p>
                    <p className="text-xl font-semibold text-green-600">£{((project.budget || 0) - (project.spent || 0)).toLocaleString()}</p>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Utilization</span>
                      <span className="font-medium text-gray-900">{budgetUtilization.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${budgetUtilization >= 90 ? 'bg-red-600' :
                            budgetUtilization >= 70 ? 'bg-yellow-600' : 'bg-green-600'
                          }`}
                        style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <UsersIcon className="w-5 h-5" />
                  Team
                </h2>
                <div className="space-y-3">
                  {project.team_members && project.team_members.length > 0 ? (
                    project.team_members.map((member, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-semibold">{member.name?.charAt(0).toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-600">{member.role}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No team members assigned</p>
                  )}
                </div>
              </div>

              {project.project_type && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Project Type</h2>
                  <p className="text-gray-700 capitalize">{project.project_type.replace('_', ' ')}</p>
                </div>
              )}

              {/* Methodology & Provenance Section */}
              {(project.creation_mode || project.source_methodology || project.initial_business_case) && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5" />
                    Project Provenance
                  </h2>

                  <div className="space-y-4">
                    {/* Creation Mode */}
                    {project.creation_mode && (
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-2">Creation Mode</p>
                        <div className="flex items-center gap-2">
                          {project.creation_mode === 'ai_autopilot' ? (
                            <>
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <RocketLaunchIcon className="w-4 h-4 text-blue-600" />
                              </div>
                              <span className="text-gray-900 font-medium">AI Autopilot</span>
                              <span className="text-xs text-gray-500 ml-2">AI-generated from simple goal</span>
                            </>
                          ) : (
                            <>
                              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <BriefcaseIcon className="w-4 h-4 text-purple-600" />
                              </div>
                              <span className="text-gray-900 font-medium">PM Powerhouse</span>
                              <span className="text-xs text-gray-500 ml-2">Translated from formal methodology</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Source Methodology */}
                    {project.source_methodology && (
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-2">Source Methodology</p>
                        <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm font-medium">
                          {project.source_methodology === 'PRINCE2_BUSINESS_CASE' && '📋 PRINCE2 Business Case'}
                          {project.source_methodology === 'LEAN_A3_REPORT' && '📊 Lean / Six Sigma A3'}
                          {project.source_methodology === 'SCRUM_EPIC' && '🏃 Scrum Epic & User Stories'}
                          {project.source_methodology === 'AGILE_USER_STORY' && '⚡ Agile User Story'}
                        </div>
                      </div>
                    )}

                    {/* Original Business Case */}
                    {project.initial_business_case && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-600">Original Document</p>
                          {project.business_case_filepath && (
                            <button
                              onClick={handleDownloadBusinessCase}
                              className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                              Download Original File
                            </button>
                          )}
                        </div>

                        {project.business_case_filename && (
                          <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <DocumentTextIcon className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium text-purple-900">{project.business_case_filename}</span>
                            </div>
                          </div>
                        )}

                        <details className="group">
                          <summary className="cursor-pointer text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2">
                            <DocumentTextIcon className="w-4 h-4" />
                            View Document Text
                            <span className="text-xs text-gray-500">(Click to expand)</span>
                          </summary>
                          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                              {project.initial_business_case}
                            </pre>
                          </div>
                        </details>
                        <p className="text-xs text-gray-500 mt-2">
                          🔒 This document is preserved for audit trail and compliance purposes
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Project Tasks</h2>
              <div className="flex gap-3">
                <button
                  onClick={handleGenerateTasks}
                  disabled={generateTasksMutation.isPending}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  <SparklesIcon className="w-5 h-5" />
                  {generateTasksMutation.isPending ? 'Generating...' : 'AI Generate Tasks'}
                </button>
                <button
                  onClick={handleAddTask}
                  className="btn-primary flex items-center gap-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add Task
                </button>
              </div>
            </div>
            <KanbanBoard
              projectId={projectId}
              tasks={tasks || []}
              onTaskClick={handleTaskClick}
              onAddTask={handleAddTask}
            />
          </div>
        )}
      </div>
    </>
  );
}
